import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import PopupMessage from '../layouts/PopupMessage';
import styles from '../css/css_pages/ReferralForm.module.css';
import config from '../../config';

const SalesReferralForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    main_phone_number: '',
    name_of_society: '',
    product_id: '',
    district_id: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    society_category_id: '',
    other_category_note: ''
  });

  const [districts, setDistricts] = useState([]);
  const [societyCategories, setSocietyCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showOtherCategory, setShowOtherCategory] = useState(false);
  const [referralExists, setReferralExists] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(false);

  const getAuthHeaders = () => {
    const token = Cookies.get('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    };
  };

  const displayMessage = (msg, error = false) => {
    setMessage(msg);
    setIsError(error);
    setShowMessage(true);
    if (!error) {
      setTimeout(() => setShowMessage(false), 5000);
    }
  };

  const closeMessage = () => {
    setShowMessage(false);
    setMessage('');
    setIsError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [districtsRes, categoriesRes, productsRes] = await Promise.all([
          fetch(`${config.API_URL}/lists/get-all-districts`, { headers }),
          fetch(`${config.API_URL}/lists/get-all-societies`, { headers }),
          fetch(`${config.API_URL}/lists/get-all-products`, { headers })
        ]);

        if (!districtsRes.ok || !categoriesRes.ok || !productsRes.ok) {
          throw new Error(t('dataFetchError'));
        }

        const districtsData = await districtsRes.json();
        const categoriesData = await categoriesRes.json();
        const productsData = await productsRes.json();

        setDistricts(districtsData);
        setSocietyCategories(categoriesData);
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        displayMessage(t('dataLoadError'), true);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [t]);

  const checkReferralExists = useCallback(async (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setReferralExists(false);
      setPhoneAvailable(false);
      return false;
    }

    setIsChecking(true);
    try {
      const [localRes, externalRes] = await Promise.all([
        fetch(
          `${config.API_URL}/sales-ref/check-phone-number-local/${cleanPhone}`,
          { headers: getAuthHeaders() }
        ),
        fetch(
          `${config.API_URL}/sales-ref/check-phone-number-external/${cleanPhone}`,
          { headers: getAuthHeaders() }
        )
      ]);

      if (localRes.status === 409 || externalRes.status === 409) {
        setReferralExists(true);
        setPhoneAvailable(false);
        return true;
      }

      if (!localRes.ok || !externalRes.ok) {
        throw new Error(t('referralCheckError'));
      }

      setPhoneAvailable(true);
      setReferralExists(false);
      return false;
    } catch (err) {
      console.error('Error checking referral:', err);
      displayMessage(t('networkErrorWhileChecking'), true);
      setPhoneAvailable(false);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const cleanPhone = formData.main_phone_number.replace(/\D/g, '');
      if (cleanPhone.length >= 10) {
        checkReferralExists(cleanPhone)
          .then(exists => {
            setReferralExists(exists);
            setPhoneAvailable(!exists);
          });
      } else {
        setReferralExists(false);
        setPhoneAvailable(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.main_phone_number, checkReferralExists]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'society_category_id') {
      setShowOtherCategory(value === 'e5305a30-3382-45ca-919b-13b0d9ea6e12');
    }

    if (name === 'main_phone_number' || name === 'contact_phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const errors = [];
    const cleanPhone = formData.main_phone_number.replace(/\D/g, '');

    if (cleanPhone.length < 10) {
      errors.push(t('phoneNumberRequired'));
    }
    if (!formData.name_of_society.trim()) {
      errors.push(t('societyNameRequired'));
    }
    if (!formData.product_id) {
      errors.push(t('productSelectionRequired'));
    }
    if (!formData.district_id) {
      errors.push(t('districtSelectionRequired'));
    }
    if (!formData.address.trim()) {
      errors.push(t('addressRequired'));
    }
    if (!formData.society_category_id) {
      errors.push(t('categorySelectionRequired'));
    }
    if (showOtherCategory && !formData.other_category_note.trim()) {
      errors.push(t('categoryDetailsRequired'));
    }

    if (errors.length > 0) {
      displayMessage(errors.join('\n'), true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (referralExists) {
      displayMessage(t('referralExistsMessage'), true);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        main_phone_number: formData.main_phone_number,
        name_of_society: formData.name_of_society.trim(),
        product_id: formData.product_id,
        district_id: formData.district_id,
        address: formData.address.trim(),
        contact_person: formData.contact_person.trim() || null,
        contact_person_phone_number: formData.contact_phone || null,
        society_category_id: formData.society_category_id,
        other_category_note: showOtherCategory ? formData.other_category_note.trim() : null
      };

      const response = await fetch(`${config.API_URL}/sales-ref/add-sales-referral`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.status === 409) {
        throw new Error(t('referralExistsMessage'));
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('submitError'));
      }

      const data = await response.json();

      displayMessage(t('referralSubmittedSuccessfully'));
      setFormData({
        main_phone_number: '',
        name_of_society: '',
        product_id: '',
        district_id: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        society_category_id: '',
        other_category_note: ''
      });
      setShowOtherCategory(false);
      setReferralExists(false);
      setPhoneAvailable(false);
    } catch (err) {
      console.error('Submission error:', err);
      displayMessage(err.message || t('submitError'), true);
      if (err.message.includes(t('referralExistsMessage'))) {
        setReferralExists(true);
        setPhoneAvailable(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewPreviousReferrals = () => {
    navigate('/previous-referrals-client');
  };

  if (loadingData) {
    return (
      <div className={styles.referralFormContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingMessage}>{t('loadingDataPleaseWait')}</div>
      </div>
    );
  }

  return (
    <div className={styles.referralFormContainer}>
      <div className={styles.formHeader}>
        <h1>{t('salesReferralFormTitle')}</h1>
        <p>{t('salesReferralFormDescription')}</p>
      </div>

      <div className={styles.formActionsTop}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={handleViewPreviousReferrals}
        >
          {t('viewPreviousReferrals')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className={styles.referralForm}>
        <div className={styles.formGroup}>
          <label htmlFor="main_phone_number">{t('societyPhoneNumber')} *</label>
          <div className={styles.inputWithIcon}>
            <input
              type="tel"
              id="main_phone_number"
              name="main_phone_number"
              value={formData.main_phone_number}
              onChange={handleChange}
              maxLength="10"
              required
              placeholder={t('enter10DigitPhoneNumber')}
              className={referralExists ? styles.inputError : phoneAvailable ? styles.inputSuccess : ''}
            />
            {isChecking && <span className={`${styles.inputIcon} ${styles.checking}`}></span>}
            {phoneAvailable && <span className={`${styles.inputIcon} ${styles.success}`}></span>}
            {referralExists && <span className={`${styles.inputIcon} ${styles.error}`}></span>}
          </div>
          {isChecking && <div className={`${styles.statusMessage} ${styles.checking}`}>{t('checkingReferralStatus')}</div>}
          {referralExists && (
            <div className={`${styles.statusMessage} ${styles.error}`}>
              {t('referralExistsMessage')}
            </div>
          )}
          {phoneAvailable && (
            <div className={`${styles.statusMessage} ${styles.success}`}>
              {t('phoneNumberAvailable')}
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="name_of_society">{t('societyName')} *</label>
          <input
            type="text"
            id="name_of_society"
            name="name_of_society"
            value={formData.name_of_society}
            onChange={handleChange}
            required
            placeholder={t('enterSocietyName')}
          />
        </div>

        <div className={styles.formGroup}>
          <label>{t('interestedProduct')} *</label>
          <div className={styles.radioGroup}>
            {products.length > 0 ? (
              products.map(product => (
                <label key={product.id} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="product_id"
                    value={product.id}
                    checked={formData.product_id === product.id}
                    onChange={handleChange}
                    required
                  />
                  <span className={styles.radioCustom}></span>
                  <span className={styles.radioLabel}>{product.product_name}</span>
                </label>
              ))
            ) : (
              <div className={styles.loadingOptions}>{t('loadingProducts')}</div>
            )}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="district_id">{t('district')} *</label>
            <select
              id="district_id"
              name="district_id"
              value={formData.district_id}
              onChange={handleChange}
              required
            >
              <option value="">{t('selectDistrict')}</option>
              {districts.map(district => (
                <option key={district.id} value={district.id}>
                  {district.district}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="society_category_id">{t('societyCategory')} *</label>
            <select
              id="society_category_id"
              name="society_category_id"
              value={formData.society_category_id}
              onChange={handleChange}
              required
            >
              <option value="">{t('selectCategory')}</option>
              {societyCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.society_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {showOtherCategory && (
          <div className={styles.formGroup}>
            <label htmlFor="other_category_note">{t('specifyCategoryDetails')} *</label>
            <input
              type="text"
              id="other_category_note"
              name="other_category_note"
              value={formData.other_category_note}
              onChange={handleChange}
              required={showOtherCategory}
              placeholder={t('enterCategoryDetails')}
            />
          </div>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="address">{t('societyAddress')} *</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            placeholder={t('enterFullAddress')}
            rows="3"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="contact_person">{t('contactPersonName')}</label>
            <input
              type="text"
              id="contact_person"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              placeholder={t('enterContactName')}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="contact_phone">{t('contactPersonPhone')}</label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              maxLength="10"
              placeholder={t('enterContactPhone')}
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={referralExists || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner}></span>
                {t('submitting')}...
              </>
            ) : (
              t('submitReferral')
            )}
          </button>
        </div>
      </form>

      {showMessage && (
        <PopupMessage
          message={message}
          onClose={closeMessage}
          isError={isError}
        />
      )}
    </div>
  );
};

export default SalesReferralForm;
